// Edit and apply (ui-spec §3.3): the user's change is a JSON merge patch (RFC 7386) over the mutable parts of the
// object (spec, labels, annotations), so after a 409 it can be reapplied onto the server's latest version without
// losing a teammate's or a controller's change to fields the user did not touch.

type Json = null | boolean | number | string | Json[] | { [k: string]: Json };

function isObject(v: unknown): v is Record<string, Json> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function equal(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** The merge patch that turns `from` into `to`: changed keys, removed keys as null, arrays replaced whole. */
export function createMergePatch(from: unknown, to: unknown): Json | undefined {
  if (!isObject(from) || !isObject(to)) return equal(from, to) ? undefined : ((to ?? null) as Json);
  const patch: Record<string, Json> = {};
  for (const k of Object.keys(from)) if (!(k in to)) patch[k] = null;
  for (const [k, v] of Object.entries(to)) {
    if (!(k in from)) patch[k] = v;
    else {
      const sub = createMergePatch(from[k], v);
      if (sub !== undefined) patch[k] = sub;
    }
  }
  return Object.keys(patch).length ? patch : undefined;
}

/** RFC 7386: objects merge, null removes, everything else replaces. */
export function applyMergePatch<T>(target: T, patch: Json | undefined): T {
  if (patch === undefined) return target;
  if (!isObject(patch)) return patch as T;
  const out: Record<string, unknown> = isObject(target) ? { ...(target as Record<string, unknown>) } : {};
  for (const [k, v] of Object.entries(patch)) {
    if (v === null) delete out[k];
    else out[k] = applyMergePatch(out[k], v);
  }
  return out as T;
}

interface Editable {
  spec?: unknown;
  metadata?: { labels?: unknown; annotations?: unknown; [k: string]: unknown };
  [k: string]: unknown;
}

/** Only the mutable parts a person edits in the YAML sheet; status and server metadata never go back. */
export function editablePart(obj: Editable): Editable {
  const meta = obj.metadata ?? {};
  return {
    ...(obj.spec !== undefined ? { spec: obj.spec } : {}),
    metadata: { ...(meta.labels !== undefined ? { labels: meta.labels } : {}), ...(meta.annotations !== undefined ? { annotations: meta.annotations } : {}) },
  };
}

/** The user's edits as a patch over what they opened. */
export function userPatch(opened: Editable, edited: Editable): Json | undefined {
  return createMergePatch(editablePart(opened), editablePart(edited));
}

/** "Reapply my changes on the latest": the user's edits onto the server's current object. */
export function reapply<T extends Editable>(latest: T, opened: Editable, edited: Editable): T {
  return applyMergePatch(latest, userPatch(opened, edited));
}
